export const LIGHT_LEVELS = ['bright', 'dim', 'dark'];
/** `radius`: a light's reach in squares (120 squares, 600 ft); `lights`: placed lights per scene; `side`: a coordinate's bound. */
export const LIGHT_LIMITS = { radius: 120, lights: 200, side: 100_000 };
export const LIGHTS_FULL = 'This scene has as many lights as it can hold. Remove some first.';
export const LIGHT_OFF_MAP = 'A light must sit on the map.';
/** The design's 3.5 torch (§1.4): 20 ft bright, 40 ft shadowy. */
export const TORCH = { bright: 4, dim: 8, on: true };
export const LIGHT_KINDS = ['light.level', 'light.add', 'light.set', 'light.remove'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isRadius = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= LIGHT_LIMITS.radius;
const isCoord = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= LIGHT_LIMITS.side;
const half = (v) => Math.round(v * 2) / 2;
const KEY_RE = /^[a-z0-9][a-z0-9._-]{0,199}$/;
/** Strict: a name of 1–200 characters with no NUL, and the site's address of the entry. */
export function parseLightFrom(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { name, segment, slug } = v;
    if (typeof name !== 'string' || !name.trim() || name.length > 200 || name.includes('\u0000'))
        return null;
    if (typeof segment !== 'string' || !KEY_RE.test(segment) || typeof slug !== 'string' || !KEY_RE.test(slug))
        return null;
    return { name: name.trim(), segment, slug };
}
/** Strict: both radii in bounds (to half a square), `bright` no more than `dim`, a light that reaches somewhere, the switch; `from` when it names an entry. */
export function parseLight(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { bright, dim, on, from } = v;
    if (!isRadius(bright) || !isRadius(dim) || typeof on !== 'boolean')
        return null;
    const src = from === undefined ? null : parseLightFrom(from);
    if (from !== undefined && !src)
        return null;
    const b = half(bright);
    const d = half(dim);
    return d > 0 && b <= d ? { bright: b, dim: d, on, ...(src ? { from: src } : {}) } : null;
}
/**
 * A spell's or item's light as a token's (issue 1151): the entry's radii (docs/MRCS/light.md, in the system's unit) in
 * squares by `per_square`, ROUNDED DOWN (a light reaches a square only when its whole distance does), on, naming its
 * entry. Null when it reaches less than a square or the system has no distance rule to say what a square is.
 */
export function lightFromEntry(light, distance, from) {
    if (!distance || !(distance.perSquare > 0))
        return null;
    const sq = (n) => Math.min(LIGHT_LIMITS.radius, Math.floor(n / distance.perSquare + 1e-9));
    const dim = sq(light.dim);
    const bright = Math.min(sq(light.bright), dim);
    return dim >= 1 ? { bright, dim, on: true, from: { ...from } } : null;
}
export function parsePlacedLight(v) {
    const light = parseLight(v);
    if (!light)
        return null;
    const { id, x, y } = v;
    const floor = v.floor;
    if (floor !== undefined && !(typeof floor === 'string' && /^[a-z0-9-]{1,40}$/.test(floor)))
        return null;
    return isUuid(id) && isCoord(x) && isCoord(y) ? { id, x: half(x), y: half(y), ...light, ...(typeof floor === 'string' ? { floor } : {}) } : null;
}
export function parseLightBody(b) {
    switch (b.kind) {
        case 'light.level': return LIGHT_LEVELS.includes(b.level) ? { kind: 'light.level', level: b.level } : null;
        case 'light.add':
        case 'light.set': {
            const light = parsePlacedLight(b.light);
            return light ? { kind: b.kind, light } : null;
        }
        case 'light.remove':
            if (!Array.isArray(b.ids) || b.ids.length < 1 || b.ids.length > LIGHT_LIMITS.lights || !b.ids.every(isUuid))
                return null;
            return { kind: 'light.remove', ids: [...new Set(b.ids)] };
        default: return null;
    }
}
export const normaliseLevel = (v) => (LIGHT_LEVELS.includes(v) ? v : 'bright');
export function normaliseLights(v) {
    if (!Array.isArray(v))
        return [];
    return v.map(parsePlacedLight).filter((l) => l !== null);
}
export const copyLights = (lights) => lights.map((l) => ({ ...l }));
/** The scene's light after an accepted event. */
export function reduceLight(state, body) {
    const lights = state.lights ?? [];
    const lightLevel = state.lightLevel ?? 'bright';
    switch (body.kind) {
        case 'light.level': return { lightLevel: body.level, lights };
        case 'light.add': return { lightLevel, lights: lights.some((l) => l.id === body.light.id) ? lights : [...lights, { ...body.light }] };
        case 'light.set': return { lightLevel, lights: lights.map((l) => (l.id === body.light.id ? { ...body.light } : l)) };
        case 'light.remove': {
            const gone = new Set(body.ids);
            return { lightLevel, lights: lights.some((l) => gone.has(l.id)) ? lights.filter((l) => !gone.has(l.id)) : lights };
        }
        default: return { lightLevel, lights };
    }
}
/** Why the DM's light event is refused (null: it stands); `extent` is the scene's map when known. */
export function lightRefusal(lights, body, extent) {
    const offMap = (l) => !!extent && (l.x > extent.width || l.y > extent.height);
    switch (body.kind) {
        case 'light.add':
            if (lights.some((l) => l.id === body.light.id))
                return 'That id is taken.';
            if (lights.length >= LIGHT_LIMITS.lights)
                return LIGHTS_FULL;
            return offMap(body.light) ? LIGHT_OFF_MAP : null;
        case 'light.set':
            if (!lights.some((l) => l.id === body.light.id))
                return 'That light is no longer on the map.';
            return offMap(body.light) ? LIGHT_OFF_MAP : null;
        case 'light.remove': return body.ids.some((id) => lights.some((l) => l.id === id)) ? null : 'Those lights are no longer on the map.';
        default: return null;
    }
}
//# sourceMappingURL=light.js.map